if (!customElements.get('wls-social-media')) {
  class WLSSocialMedia extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        socialMediaSlider: 'js-wls-social-media-slider',
        changeSlide: 'data-change-slide',
        autoplayInView: 'data-autoplay-in-view',
        muteButton: 'js-mute-button',
        dataVideo: 'data-video',
        playing: 'playing',
        hidden: 'hidden!',
        socialMediaFadeSlide: 'js-social-media-fade-slider',
        sectionId: `${this.getAttribute('data-section-id')}`,
      };

      this.selectors = {
        socialMediaSlider: this.querySelector(`.${this.classes.socialMediaSlider}`),
        videos: this.querySelectorAll(`[${this.classes.dataVideo}] video`),
        socialMediaFadeSlides: this.querySelectorAll(`.${this.classes.socialMediaFadeSlide}`),
      };

      this.videoStates = new Map();
      this.activeVideo = null;

      // Parse video settings from attribute
      const settingsString = this.getAttribute('data-video-settings');
      try {
        this.videoSettings = settingsString ? JSON.parse(settingsString) : {};
      } catch (e) {
        console.warn(e);
        this.videoSettings = {};
      }
    }
    /**
     * Lifecycle method called when the element is added to the DOM.
     * It sets up event listeners for each tag to handle tag selection.
     *
     * @returns {void}
     */
    connectedCallback() {
      this.initializeSlider();

      if (this.videoSettings.autoplay_in_view) {
        this.initIntersectionObserver();
      } else if (this.videoSettings.change_slide) {
        this.initManualVideoControl();
      } else {
        this.initManualVideoControl();
      }

      this.initMuteButtons();
    }

    /**
     * Initialize Swiper slider. Handles slider initialization and configuration.
     *
     * @returns {void}
     */
    initializeSlider() {
      const totalSlides = this.selectors.socialMediaSlider?.querySelectorAll('.swiper-slide')?.length || 0;
      const settingsElement = document.getElementById(`CarouselSettings-${this.classes.sectionId}`);
      const settings = JSON.parse(settingsElement.textContent);
      const { slidesPerView, loop, centeredSlides } = settings;

      const shouldCenterSlides = {
        base: totalSlides > slidesPerView.base + 2,
        tablet: totalSlides >= slidesPerView.tablet + 2,
        desktop: totalSlides >= slidesPerView.desktop + 2,
      };

      const swiper = new Swiper(this.selectors.socialMediaSlider, {
        slidesPerView: slidesPerView.base,
        spaceBetween: 8,
        centeredSlides: centeredSlides.base ? shouldCenterSlides.base : false,
        loop: loop.base ? totalSlides > slidesPerView.base : false,
        centerInsufficientSlides: true,
        breakpoints: {
          768: {
            slidesPerView: slidesPerView.tablet,
            spaceBetween: 16,
            centeredSlides: centeredSlides.tablet ? shouldCenterSlides.tablet : false,
            loop: loop.tablet ? totalSlides > slidesPerView.tablet : false,
          },
          1024: {
            slidesPerView: slidesPerView.desktop,
            spaceBetween: 16,
            centeredSlides: centeredSlides.desktop ? shouldCenterSlides.desktop : false,
            loop: loop.desktop ? totalSlides > slidesPerView.desktop : false,
          },
        },
        on: {
          slideChange: (e) => {
            if (this.videoSettings.change_slide && this.activeVideo) {
              this.activeVideo.pause();
              this.activeVideo.classList.remove(this.classes.playing);
              this.activeVideo = null;
            }

            if (this.videoSettings.autoplay_in_view) {
              this.pauseVideos();
              this.playActiveSlideVideo(e.activeIndex);
            }
          },
        },
      });

      const screenWidth = window.innerWidth;
      let shouldHideFadeSlides = false;

      if (screenWidth >= 1024 && !shouldCenterSlides.desktop) {
        shouldHideFadeSlides = true;
      } else if (screenWidth >= 768 && !shouldCenterSlides.tablet) {
        shouldHideFadeSlides = true;
      } else if (screenWidth < 768 && !shouldCenterSlides.base) {
        shouldHideFadeSlides = true;
      }

      if (shouldHideFadeSlides) {
        this.selectors.socialMediaFadeSlides.forEach((el) => el.classList.add(this.classes.hidden));
      }
    }
    /**
     * Play video on the active slide
     * @param {number} activeIndex - Index of the active slide
     */
    playActiveSlideVideo(activeIndex) {
      const activeSlide = this.selectors.socialMediaSlider.querySelectorAll('.swiper-slide')[activeIndex];
      const activeVideo = activeSlide.querySelector('video');
      if (activeVideo) {
        activeVideo.play().catch((err) => {
          console.warn(err);
        });
        activeVideo.classList.add(this.classes.playing);
      }
    }

    /**
     * Initialize mute buttons. Display mute button for each video and toggle mute on click.
     *
     * @returns {void}
     */
    initMuteButtons() {
      this.selectors.videos.forEach((video) => {
        const container = video.closest(`[${this.classes.dataVideo}]`);
        if (!container) return;

        const muteButton = container.querySelector(`.${this.classes.muteButton}`);
        if (!muteButton) return;

        this.updateMuteButtonClass(video, muteButton);

        muteButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          video.muted = !video.muted;
          this.updateMuteButtonClass(video, muteButton);
        });
      });
    }

    /**
     * Update mute button class based on video mute state.
     *
     * @param {HTMLVideoElement} - The video element
     * @param {HTMLElement} - The mute button element
     */
    updateMuteButtonClass(video, muteButton) {
      if (video.muted) {
        muteButton.classList.add('muted');
        muteButton.classList.remove('unmuted');
      } else {
        muteButton.classList.remove('muted');
        muteButton.classList.add('unmuted');
      }
    }

    /**
     * Initialize IntersectionObserver to play videos when they are in view.
     *
     * @returns {void}
     */
    initIntersectionObserver() {
      const options = {
        root: null,
        threshold: 0.6,
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const element = entry.target;
          if (entry.isIntersecting) {
            const activeSlide = element.querySelector('.swiper-slide-active');
            if (activeSlide) {
              const activeVideo = activeSlide.querySelector('video');
              if (activeVideo) {
                const lastTime = this.videoStates.get(activeVideo) || 0;
                activeVideo.currentTime = lastTime;
                activeVideo.play().catch((err) => {
                  console.warn('Error playing video:', err);
                });
                activeVideo.classList.add(this.classes.playing);
              }
            }
          } else {
            this.pauseVideos();
          }
        });
      }, options);

      observer.observe(this);
    }

    /**
     * Play all videos. When a video is played, it is added to the videoStates map to keep track of the last played time.
     *
     * @returns {void}
     */
    playVideos() {
      this.selectors.videos.forEach((video) => {
        const lastTime = this.videoStates.get(video) || 0;
        video.currentTime = lastTime;
        video.play();
      });
    }

    /**
     * Pause all videos. When a video is paused, it is added to the videoStates map to keep track of the last played time.
     *
     * @returns {void}
     * */
    pauseVideos() {
      this.selectors.videos.forEach((video) => {
        this.videoStates.set(video, video.currentTime);
        video.pause();
      });
    }
    /**
     * Initialize manual video control. Play or pause video on click.
     *
     * @returns {void}
     * */
    initManualVideoControl() {
      this.selectors.videos.forEach((video) => {
        video.addEventListener('click', () => {
          const isPlaying = !video.paused && !video.ended && video.readyState > 2;

          if (isPlaying) {
            video.pause();
            video.classList.remove(this.classes.playing);
            if (this.activeVideo === video) {
              this.activeVideo = null;
            }
          } else {
            if (this.activeVideo && this.activeVideo !== video) {
              this.activeVideo.pause();
              this.activeVideo.classList.remove(this.classes.playing);
            }

            video
              .play()
              .then(() => {
                video.classList.add(this.classes.playing);
                this.activeVideo = video;
              })
              .catch((err) => {
                console.warn(err);
              });
          }
        });
      });
    }
  }

  window.customElements.define('wls-social-media', WLSSocialMedia);
}
